import { useEffect, useState } from "react";
import { Moon, Sun, Monitor, Check } from "lucide-react";
import { IconButton } from "@/components/IconButton";
import { Menu } from "@/components/Menu";

type ThemeChoice = "system" | "light" | "dark";

const KEY = "theme";

function getChoice(): ThemeChoice {
  if (typeof window === "undefined") return "system";
  const v = localStorage.getItem(KEY);
  return v === "light" || v === "dark" ? v : "system";
}

function systemDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function apply(choice: ThemeChoice) {
  const dark = choice === "dark" || (choice === "system" && systemDark());
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
}

const OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

/**
 * Theme switcher. Defaults to the OS setting ("system"); the user can override
 * with Light or Dark. The choice persists in localStorage and is re-applied
 * before first paint by the inline boot script in the Inertia layout. While on
 * "system", live OS changes are followed.
 */
export function ThemePicker() {
  const [choice, setChoice] = useState<ThemeChoice>(getChoice);

  useEffect(() => {
    apply(choice);
    if (choice !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [choice]);

  function pick(next: ThemeChoice) {
    if (next === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
    setChoice(next);
  }

  const effectiveDark = choice === "dark" || (choice === "system" && systemDark());

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <IconButton aria-label="Theme">
          {effectiveDark ? <Moon /> : <Sun />}
        </IconButton>
      </Menu.Trigger>
      <Menu.Content align="end" sideOffset={8} className="min-w-[10rem]">
        <Menu.Label>Theme</Menu.Label>
        {OPTIONS.map((o) => (
          <Menu.Item key={o.value} onSelect={() => pick(o.value)}>
            <o.icon />
            {o.label}
            {choice === o.value && <Check className="ml-auto" />}
          </Menu.Item>
        ))}
      </Menu.Content>
    </Menu.Root>
  );
}
